// @ts-check

/**
 * @file Renders a reusable country select field.
 */

import {useForm} from "@kaspernj/api-maker/build/form"
import gettextConfig from "gettext-universal/build/src/config.js"
import PropTypes from "prop-types"
import propTypesExact from "prop-types-exact"
import {memo, useMemo} from "react"
import {View} from "react-native"
import {shapeComponent, ShapeComponent} from "set-state-compare/build/shape-component.js"
import errorMessage from "../shared/error-message"
import {Organization} from "../frontend-models/organization"
import Select from "./select"
import Text from "@kaspernj/api-maker/build/utils/text"
import useThemeColors, {useThemeName} from "../shared/use-theme-colors"
import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
const SUPPORTED_LOCALES = new Set(["da", "de", "en"])

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @type {Record<string, object>} */
const styles = {}

/** @typedef {{text: string, value: string}} CountryOption */

/** @type {{options: CountryOption[], locale: string} | null} */
let cachedCountries = null
/** @type {Promise<CountryOption[]> | null} */
let inflightCountriesPromise = null

/**
 * @typedef {object} CountrySelectState
 * @property {CountryOption[]} options Loaded country options.
 * @property {string} selectedValue Selected country code.
 * @property {string | null} loadError Country loading error text.
 */

/** @typedef {{[key: string]: unknown, defaultValue?: string, label: string, name: string, placeholder?: string, testID: string}} CountrySelectProps */

/**
 * Cross-screen country dropdown backed by the Organization.countries() API.
 * Shares one cached options list per locale across all instances so the
 * backend round-trip happens at most once per page load.
 * @augments {ShapeComponent<CountrySelectProps, CountrySelectState>}
 */
class CountrySelect extends ShapeComponent {
  /** @type {ReturnType<typeof useForm> | null} */
  form = null
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()
  /** @type {CountrySelectState} */
  state = {
    loadError: null,
    options: cachedCountries?.options ?? [],
    selectedValue: ""
  }
  static propTypes = propTypesExact({
    defaultValue: PropTypes.string,
    label: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    placeholder: PropTypes.string,
    testID: PropTypes.string.isRequired
  })
  static defaultProps = {
    defaultValue: undefined,
    placeholder: undefined
  }

  /** Initializes form binding and country option loading. */
  setup() {
    useLocale()

    this.form = useForm()
    this.colors = useThemeColors()
    this.themeName = useThemeName()

    // Mirror FormSelect: prime the Form value with the initial defaultValue
    // exactly once on mount. Doing this on every render would overwrite any
    // user selection back to the default.
    useMemo(() => {
      const initial = this.p.defaultValue || ""
      this.s.selectedValue = initial
      if (this.form) {
        this.form.setValue(this.p.name, initial)
      }
    }, [])

    // Fire-and-forget from setup — loadCountriesIfNeeded handles its own
    // errors by stashing them on state instead of propagating an unhandled
    // rejection up to React's error boundary.
    void this.loadCountriesIfNeeded()
  }

  /**
   * Loads country options for the active locale.
   * @returns {Promise<void>} Resolves when country options finish loading.
   */
  loadCountriesIfNeeded = async () => {
    const locale = currentCountriesLocale()
    if (cachedCountries && cachedCountries.locale === locale) {
      if (this.s.options.length === 0) this.s.options = cachedCountries.options
      return
    }
    inflightCountriesPromise ||= (async () => {
      const response = await Organization.countries({
        locale
      })
      const options = response.countries.map((country) => ({
        text: country.countryName,
        value: country.countryCode
      }))
      cachedCountries = {
        locale,
        options
      }
      return options
    })()
    try {
      const options = await inflightCountriesPromise
      if (!this.isMounted()) return
      this.s.loadError = null
      this.s.options = options
    } catch (error) {
      // Reset the inflight cache so a later re-render can retry instead
      // of pinning the failed promise forever.
      inflightCountriesPromise = null
      if (this.isMounted()) {
        this.s.loadError = errorMessage(error)
      }
    } finally {
      if (cachedCountries) inflightCountriesPromise = null
    }
  }

  /**
   * Applies the selected country value.
   * @param {(string | number)[] | string | number | undefined} value Selected value.
   * @returns {void} Nothing.
   */
  onChangeValue = (value) => {
    const next = typeof value === "string" ? value : ""
    this.s.selectedValue = next
    if (this.form) {
      this.form.setValue(this.p.name, next)
    }
  }

  /**
   * Renders the country select.
   * @returns {ReactNode} Country select content.
   */
  render() {
    const {label, placeholder, testID} = this.p
    const {loadError, options, selectedValue} = this.s
    const themeName = this.tt.themeName
    const colors = this.tt.colors
    return (
      <View testID={`${testID}/wrap`}>
        <Text
          style={styles[`label-${themeName}`] ||= {
            color: colors.textMuted,
            fontSize: 14,
            fontWeight: 600
          }}
          testID={`${testID}/label`}
        >
          {label}
        </Text>
        {loadError &&
          <Text
            style={styles[`error-${themeName}`] ||= {
              color: colors.dangerText,
              fontSize: 13,
              marginTop: 4
            }}
            testID={`${testID}/loadError`}
          >
            {loadError}
          </Text>
        }
        <View
          style={styles.selectWrap ||= {
            marginTop: 6
          }}
          testID={testID}
        >
          <Select
            onChangeValue={this.tt.onChangeValue}
            options={options}
            placeholder={placeholder || _("Select country")}
            search
            testID={`${testID}/select`}
            values={selectedValue ? [selectedValue] : []}
          />
        </View>
      </View>
    )
  }
}

/** @type {NamedExoticComponent<CountrySelectProps>} */
const CountrySelectComponent = memo(shapeComponent(CountrySelect))

export default CountrySelectComponent

/**
 * Returns the active app locale for country options.
 * @returns {string} ISO locale matching the supported backend countries.
 */
function currentCountriesLocale() {
  const appLocale = (gettextConfig.getLocale() || "").slice(0, 2).toLowerCase()
  if (SUPPORTED_LOCALES.has(appLocale)) return appLocale
  const browserLocale = (globalThis?.navigator?.language || "")
    .slice(0, 2)
    .toLowerCase()
  if (SUPPORTED_LOCALES.has(browserLocale)) return browserLocale
  return "en"
}
